import { Button } from '../../ui/button';
import type { FieldType, ValueType } from './InformationCard.type';
import { formatDate } from '../../../lib/date-formatting';
import { CheckIcon, SquarePenIcon, XIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ZodError } from 'zod';
import { InlineInput } from './InlineInput';
import { InformationFieldCell } from './InformationFieldCell';
import { InformationFieldWrapper } from './InformationFieldWrapper';
import { InformationFieldLabel } from './InformationFieldLabel';
import { useTranslation } from 'react-i18next';
import { buildFieldSchema, type FieldValidationRules } from './buildFieldSchema';
import { Group, Stack } from '@folio/components/ui/flex';

const getValueForNumberInput = (
  value: string | number | boolean | undefined,
): number | undefined => {
  if (typeof value === 'number') {
    return value;
  }

  return undefined;
};

const getValueForStringInput = (
  value: string | number | boolean | undefined,
): string | undefined => {
  if (typeof value === 'string') {
    return value;
  }
  return undefined;
};

const getValueForBooleanInput = (
  value: string | number | boolean | undefined,
): boolean | undefined => {
  if (typeof value === 'boolean') {
    return value;
  }

  return undefined;
};

export const defaultFormatValue: Record<
  FieldType,
  (value: string | number | boolean | undefined) => string | undefined
> = {
  number: (value: string | number | boolean | undefined) => {
    if (typeof value === 'number') {
      return value.toString();
    }
    return undefined;
  },
  text: (value: string | number | boolean | undefined) => {
    if (typeof value === 'string') {
      return value;
    }
    return undefined;
  },
  date: (value: string | number | boolean | undefined) => {
    if (typeof value !== 'string' || !value) {
      return undefined;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : formatDate(date);
  },
  email: (value: string | number | boolean | undefined) => {
    if (typeof value === 'string') {
      return value;
    }
    return undefined;
  },
  tel: (value: string | number | boolean | undefined) => {
    if (typeof value === 'string') {
      return value;
    }
    return undefined;
  },
  postcode: (value: string | number | boolean | undefined) => {
    if (typeof value === 'string') {
      return value;
    }
    return undefined;
  },
  siret: (value: string | number | boolean | undefined) => {
    if (typeof value === 'string') {
      return value;
    }
    return undefined;
  },
  boolean: (value: string | number | boolean | undefined) => {
    if (typeof value === 'boolean') {
      return value ? 'Oui' : 'Non';
    }
    return undefined;
  },
  array: () => undefined,
  select: (value: string | number | boolean | undefined) => {
    if (typeof value === 'string') {
      return value;
    }
    return undefined;
  },
};

type InformationFieldProps = {
  fieldKey: string;
  value: string | number | boolean | undefined;
  formatValue?: (value: string | number | boolean | undefined) => string | undefined;
  editionMode: 'INLINE' | 'FORM' | 'DISABLED';
  type: FieldType;
  options?: string[];
  validationRules?: FieldValidationRules;
  redirectToForm?: () => Promise<void> | void;
  updateEntity: (key: string, value: ValueType) => Promise<void> | void;
};

function InformationFieldValue({
  fieldKey,
  value,
  editionMode,
  type,
  options,
  validationRules,
  redirectToForm,
  updateEntity,
  formatValue = defaultFormatValue[type],
}: InformationFieldProps) {
  const { t } = useTranslation();

  const [newValue, setNewValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const schema = useMemo(() => buildFieldSchema(type, validationRules), [type, validationRules]);

  const save = async (value: string | number | boolean | undefined) => {
    if (value === undefined) {
      return;
    }

    await updateEntity(fieldKey, value);
  };

  const { mutate, isPending, error } = useMutation({
    mutationFn: async () => {
      setValidationError(null);

      await schema.parseAsync(newValue);

      if (type === 'number') {
        await save(getValueForNumberInput(newValue));
      } else if (type === 'boolean') {
        await save(getValueForBooleanInput(newValue));
      } else {
        await save(getValueForStringInput(newValue));
      }
    },
    onSuccess: () => {
      setIsEditing(false);
    },
    onError: error => {
      if (error instanceof ZodError) {
        setValidationError(error.issues[0].message);
      }
    },
  });

  // A bit of "magic" here to handle the backend ENUM fields that don't have a match in the frontend for now
  // Should be removed for something more explicit when harmonizing field types accross the platform
  const valueTranslation = t([`fields.${fieldKey}.options.${value}`, `options.${value}`, 'empty']);
  const formattedValue = valueTranslation
    ? valueTranslation
    : formatValue
      ? formatValue(value)
      : value;

  if (editionMode === 'DISABLED') {
    return <InformationFieldCell>{formattedValue}</InformationFieldCell>;
  }

  if (editionMode === 'FORM') {
    return (
      <InformationFieldCell className="py-2 min-h-14 align-bottom">
        <Group justify="between" gap={0}>
          <div className="flex-1">{formattedValue}</div>

          <Button
            name="mta-edition"
            variant="ghost"
            size="sm"
            icon={<SquarePenIcon className="w-4 h-4" />}
            onClick={redirectToForm}
          >
            {t('informationField.editContract')}
          </Button>
        </Group>
      </InformationFieldCell>
    );
  }

  if (editionMode !== 'INLINE') {
    throw new Error(`Unsupported edition mode: ${editionMode}`);
  }

  if (isEditing) {
    return (
      <InformationFieldCell>
        <Stack gap={2}>
          <Group gap={2}>
            <InlineInput
              type={type}
              value={newValue}
              onChange={setNewValue}
              className="h-[36px] min-w-0"
              fieldKey={fieldKey}
              options={options}
            />
            <Button
              name="validate-inline-change"
              variant="outline"
              size="sm"
              onClick={() => {
                if (newValue !== undefined) {
                  mutate();
                }
              }}
              loading={isPending}
            >
              <CheckIcon className="w-4 h-4" />
            </Button>
            <Button
              name="cancel-inline-change"
              variant="outline"
              size="sm"
              onClick={() => {
                setNewValue(value);
                setIsEditing(false);
              }}
              loading={isPending}
            >
              <XIcon className="w-4 h-4" />
            </Button>
          </Group>
          {validationError && (
            <div className="text-sm font-medium text-destructive">{validationError}</div>
          )}
          {error && !validationError && (
            <div className="text-sm font-medium text-destructive">{error.message}</div>
          )}
        </Stack>
      </InformationFieldCell>
    );
  }

  return (
    <InformationFieldCell>
      <Group justify="between" gap={2}>
        <div className="min-w-0 overflow-hidden text-ellipsis">{formattedValue}</div>
        <Button
          name="edit-inline-field"
          variant="ghost"
          onClick={() => setIsEditing(!isEditing)}
          size="sm"
        >
          <SquarePenIcon className="w-4 h-4" />
        </Button>
      </Group>
    </InformationFieldCell>
  );
}

export function InformationField({
  fieldKey,
  label,
  productId,
  value,
  editionMode,
  type,
  options,
  validationRules,
  redirectToForm,
  updateEntity,
  formatValue,
}: {
  fieldKey: string;
  label?: string;
  productId?: string;
  value: string | number | boolean | undefined;
  formatValue?: (value: string | number | boolean | undefined) => string | undefined;
  editionMode: 'INLINE' | 'FORM' | 'DISABLED';
  type: FieldType;
  options?: string[];
  validationRules?: FieldValidationRules;
  redirectToForm?: () => Promise<void> | void;
  updateEntity: (key: string, value: ValueType) => Promise<void> | void;
}) {
  const { t } = useTranslation();

  const labelLookupKeys = [
    ...(productId ? [`${productId}.fields.${fieldKey}.label`] : []),
    `fields.${fieldKey}.informationField.label`,
    `fields.${fieldKey}.label`,
  ];

  return (
    <InformationFieldWrapper>
      <InformationFieldLabel>{label ?? t(labelLookupKeys)}</InformationFieldLabel>
      <InformationFieldValue
        fieldKey={fieldKey}
        redirectToForm={redirectToForm}
        updateEntity={updateEntity}
        editionMode={editionMode}
        type={type}
        options={options}
        value={value}
        formatValue={formatValue}
        validationRules={validationRules}
      />
    </InformationFieldWrapper>
  );
}
